import { configureStore } from "@reduxjs/toolkit";
import { isPlainObject } from "@reduxjs/toolkit";
import authReducer from "./slices/authSlice";
import registrationReducer from "./slices/registrationSlice";
import patientsReducer from "./slices/patientsSlice";
import calendarReducer from "./slices/calendarSlice";
import uiReducer from "./slices/uiSlice";
import { therapistApi } from "./services/therapistApi";
import { patientsApi } from "./services/patientsApi";
import { calendarApi } from "./services/calendarApi";
import { tasksApi } from "./services/tasksApi";
import { activityLogsApi } from "./services/activityLogsApi";
import { formsApi } from "./services/formsApi";
import { treatmentsApi } from "./services/treatmentsApi";
import { notificationsApi } from "./services/notificationsApi";
import { financeApi } from "./services/financeApi";
import { leadsApi } from "./services/leadsApi";
import { patientFilesApi } from "./services/patientFilesApi";
import { nivApi } from "./services/nivApi";
import { gamesApi } from "./services/gamesApi";
import { roomsApi } from "./services/roomsApi";
import { liveSessionsApi } from "./services/liveSessionsApi";
import { questionnairesApi } from "./services/questionnairesApi";
import { clinicFilesApi } from "./services/clinicFilesApi";
import { supportApi } from "./services/supportApi";
import { nivSupportApi } from "./services/nivSupportApi";
import { monthlyReportsApi } from "./services/monthlyReportsApi";
import { punchCardsApi } from "./services/punchCardsApi";
import { patientReportsApi } from "./services/patientReportsApi";
import { nutritionFoodsApi } from "./services/nutritionFoodsApi";
import { measurementsApi } from "./services/measurementsApi";
import { menusApi } from "./services/menusApi";
import { dailyReportsApi } from "./services/dailyReportsApi";
import { growApi } from "./services/growApi";

// Custom serializable check that ignores Firestore Timestamps
const isSerializable = (value) => {
  // Allow Firestore Timestamps (they have seconds and nanoseconds properties)
  if (
    value &&
    typeof value === "object" &&
    "seconds" in value &&
    "nanoseconds" in value
  ) {
    return true;
  }
  // Allow Date objects (we'll serialize them to ISO strings)
  if (value instanceof Date) {
    return true;
  }
  // Default check for plain objects, arrays, primitives
  return (
    isPlainObject(value) ||
    Array.isArray(value) ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  );
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    registration: registrationReducer,
    patients: patientsReducer,
    calendar: calendarReducer,
    ui: uiReducer,
    [therapistApi.reducerPath]: therapistApi.reducer,
    [patientsApi.reducerPath]: patientsApi.reducer,
    [calendarApi.reducerPath]: calendarApi.reducer,
    [tasksApi.reducerPath]: tasksApi.reducer,
    [activityLogsApi.reducerPath]: activityLogsApi.reducer,
    [formsApi.reducerPath]: formsApi.reducer,
    [treatmentsApi.reducerPath]: treatmentsApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [financeApi.reducerPath]: financeApi.reducer,
    [leadsApi.reducerPath]: leadsApi.reducer,
    [patientFilesApi.reducerPath]: patientFilesApi.reducer,
    [clinicFilesApi.reducerPath]: clinicFilesApi.reducer,
    [nivApi.reducerPath]: nivApi.reducer,
    [gamesApi.reducerPath]: gamesApi.reducer,
    [roomsApi.reducerPath]: roomsApi.reducer,
    [liveSessionsApi.reducerPath]: liveSessionsApi.reducer,
    [questionnairesApi.reducerPath]: questionnairesApi.reducer,
    [supportApi.reducerPath]: supportApi.reducer,
    [nivSupportApi.reducerPath]: nivSupportApi.reducer,
    [monthlyReportsApi.reducerPath]: monthlyReportsApi.reducer,
    [punchCardsApi.reducerPath]: punchCardsApi.reducer,
    [patientReportsApi.reducerPath]: patientReportsApi.reducer,
    [nutritionFoodsApi.reducerPath]: nutritionFoodsApi.reducer,
    [measurementsApi.reducerPath]: measurementsApi.reducer,
    [menusApi.reducerPath]: menusApi.reducer,
    [dailyReportsApi.reducerPath]: dailyReportsApi.reducer,
    [growApi.reducerPath]: growApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore all RTK Query actions - they handle serialization internally
        ignoredActions: [
          "registration/setLogo",
          // Ignore all RTK Query action patterns
          /^therapistApi\//,
          /^patientsApi\//,
          /^calendarApi\//,
          /^tasksApi\//,
          /^activityLogsApi\//,
          /^formsApi\//,
          /^treatmentsApi\//,
          /^notificationsApi\//,
          /^financeApi\//,
          /^leadsApi\//,
          /^patientFilesApi\//,
          /^clinicFilesApi\//,
          /^nivApi\//,
          /^gamesApi\//,
          /^roomsApi\//,
          /^liveSessionsApi\//,
          /^questionnairesApi\//,
          /^supportApi\//,
          /^nivSupportApi\//,
          /^monthlyReportsApi\//,
          /^punchCardsApi\//,
          /^patientReportsApi\//,
          /^nutritionFoodsApi\//,
          /^measurementsApi\//,
          /^menusApi\//,
          /^dailyReportsApi\//,
          /^growApi\//,
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: [
          "payload.file",
          "meta.arg",
          "meta.baseQueryMeta",
          "payload.createdAt",
          "payload.updatedAt",
          "payload.registrationDate",
          "payload.trialStartDate",
          "payload.trialEndDate",
          "payload.dateOfBirth",
          "payload.joinDate",
          "payload.startTime",
          "payload.endTime",
          "payload.lastDoc",
          "payload.data.lastDoc",
        ],
        // Ignore RTK Query cache paths and auth profile timestamps
        ignoredPaths: [
          "registration.logoFile",
          "auth.therapistProfile.createdAt",
          "auth.therapistProfile.updatedAt",
          "auth.therapistProfile.registrationDate",
          "auth.therapistProfile.trialStartDate",
          "auth.therapistProfile.trialEndDate",
          "auth.clinicProfile.createdAt",
          "auth.clinicProfile.updatedAt",
          // Ignore RTK Query caches entirely
          "patientsApi.queries",
          "calendarApi.queries",
          "therapistApi.queries",
          "tasksApi.queries",
          "activityLogsApi.queries",
          "formsApi.queries",
          "treatmentsApi.queries",
          "notificationsApi.queries",
          "financeApi.queries",
          "leadsApi.queries",
          "patientFilesApi.queries",
          "clinicFilesApi.queries",
          "nivApi.queries",
          "gamesApi.queries",
          "roomsApi.queries",
          "liveSessionsApi.queries",
          "questionnairesApi.queries",
          "supportApi.queries",
          "nivSupportApi.queries",
          "monthlyReportsApi.queries",
          "punchCardsApi.queries",
          "patientReportsApi.queries",
          "nutritionFoodsApi.queries",
          "measurementsApi.queries",
          "menusApi.queries",
          "dailyReportsApi.queries",
          "growApi.queries",
        ],
        // Use custom serializable check
        isSerializable,
      },
    }).concat(
      therapistApi.middleware,
      patientsApi.middleware,
      calendarApi.middleware,
      tasksApi.middleware,
      activityLogsApi.middleware,
      formsApi.middleware,
      treatmentsApi.middleware,
      notificationsApi.middleware,
      financeApi.middleware,
      leadsApi.middleware,
      patientFilesApi.middleware,
      clinicFilesApi.middleware,
      nivApi.middleware,
      gamesApi.middleware,
      roomsApi.middleware,
      liveSessionsApi.middleware,
      questionnairesApi.middleware,
      supportApi.middleware,
      nivSupportApi.middleware,
      monthlyReportsApi.middleware,
      punchCardsApi.middleware,
      patientReportsApi.middleware,
      nutritionFoodsApi.middleware,
      measurementsApi.middleware,
      menusApi.middleware,
      dailyReportsApi.middleware,
      growApi.middleware
    ),
});

export default store;
